/**
 * InviteSubModal — invite subs to a project.
 *
 * Two modes (tabbed):
 *   1. "By email" (default) — type email + company → creates vendor + sends invite
 *   2. "Address book"       — pick from existing contractor vendors
 *
 * Uses:
 *   POST /api/contractor/vendors                   — create/upsert vendor
 *   GET  /api/contractor/vendors                   — address book
 *   POST /api/contractor/projects/:id/invite       — send invites
 * canonical: PR-V4-SUB-PORTAL
 */
import { useState, type CSSProperties } from 'react'
import { useQuery, useQueryClient }         from '@tanstack/react-query'
import {
  fetchContractorVendors,
  createContractorVendor,
  inviteVendorsToProject,
} from '../../../api/projects'
import type { ContractorVendor }            from '../../../api/types'
import ModalPortal, { modalBoxStyle }     from '../../../components/ModalPortal'
import { track } from '../../../lib/track'

const errStyle = { fontSize: 12, color: 'var(--red)', margin: '8px 16px 0' } as const
const ftrStyle = {
  display: 'flex', justifyContent: 'flex-end', gap: 8,
  padding: '12px 16px', borderTop: '1px solid var(--line)',
} as const
const chkBase: CSSProperties = {
  width: 18, height: 18, borderRadius: 4, border: '1px solid var(--line2)',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  fontSize: 11, fontWeight: 700, flexShrink: 0,
  color: 'var(--ink)', background: 'var(--surface)',
}

type Tab = 'email' | 'book'

interface Props {
  projectId: number
  onClose:   () => void
}

export default function InviteSubModal({ projectId, onClose }: Props) {
  const qc = useQueryClient()

  /* ── tab ── */
  const [tab, setTab] = useState<Tab>('email')

  /* ── "by email" mode ── */
  const [email,    setEmail]    = useState('')
  const [company,  setCompany]  = useState('')
  const [sending,  setSending]  = useState(false)
  const [sent,     setSent]     = useState(false)
  const [err,      setErr]      = useState<string | null>(null)

  /* ── address book mode ── */
  const [search,   setSearch]   = useState('')
  const [selected, setSelected] = useState<number[]>([])
  const [bookSend, setBookSend] = useState(false)

  const { data: vendors = [], isPending } = useQuery({
    queryKey: ['contractor-vendors'],
    queryFn:  fetchContractorVendors,
    staleTime: 60_000,
  })

  const filtered = vendors.filter((v: ContractorVendor) =>
    !search ||
    (v.company_name ?? '').toLowerCase().includes(search.toLowerCase()) ||
    (v.contact_name ?? '').toLowerCase().includes(search.toLowerCase()) ||
    (v.trade ?? '').toLowerCase().includes(search.toLowerCase())
  )

  function toggleVendor(id: number): void {
    setSelected(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    )
  }

  /* ── "by email" submit ── */
  async function handleEmailInvite(confirmSelf = false): Promise<void> {
    if (!email.trim() || !company.trim() || sending) return
    setSending(true)
    setErr(null)
    try {
      const vendor = await createContractorVendor({ email: email.trim(), company_name: company.trim() })
      await inviteVendorsToProject(projectId, [vendor.id], { confirmSelfInvite: confirmSelf })
      track('sub_invited', { project_id: projectId, count: 1 })
      setSent(true)
      qc.invalidateQueries({ queryKey: ['contractor-vendors'] })
      qc.invalidateQueries({ queryKey: ['project-trades', projectId] })
      qc.invalidateQueries({ queryKey: ['subs-vendors'] })
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Send failed'
      if (/own account|self_contractor|sign you out/i.test(msg) && !confirmSelf) {
        const ok = window.confirm(
          'This is your own account — the portal link will sign you out of your contractor session; continue?',
        )
        if (ok) {
          setSending(false)
          return handleEmailInvite(true)
        }
      }
      setErr(msg)
    } finally {
      setSending(false)
    }
  }

  /* ── address book submit ── */
  async function handleBookInvite(confirmSelf = false): Promise<void> {
    if (!selected.length || bookSend) return
    setBookSend(true)
    setErr(null)
    try {
      await inviteVendorsToProject(projectId, selected, { confirmSelfInvite: confirmSelf })
      track('sub_invited', { project_id: projectId, count: selected.length })
      setSent(true)
      qc.invalidateQueries({ queryKey: ['project-trades', projectId] })
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Send failed'
      if (/own account|self_contractor|sign you out/i.test(msg) && !confirmSelf) {
        const ok = window.confirm(
          'This is your own account — the portal link will sign you out of your contractor session; continue?',
        )
        if (ok) {
          setBookSend(false)
          return handleBookInvite(true)
        }
      }
      setErr(msg)
    } finally {
      setBookSend(false)
    }
  }

  const sentCount = tab === 'email' ? 1 : selected.length

  return (
    <ModalPortal
      testId="invite-sub-modal-backdrop"
      onBackdropClick={onClose}
    >
      <div style={modalBoxStyle} data-testid="invite-sub-modal" onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', borderBottom: '1px solid var(--line)' }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--ink)' }}>Invite subcontractors</div>
          <button
            type="button"
            onClick={onClose}
            data-testid="invite-sub-close-btn"
            style={{ background: 'none', border: 'none', fontSize: 16, cursor: 'pointer', color: 'var(--ink3)' }}
          >
            ✕
          </button>
        </div>

        {sent ? (
          <div style={{ padding: 20 }}>
            <div style={{ color: 'var(--green)', fontWeight: 600, marginBottom: 8 }}>
              ✓ Invite{sentCount !== 1 ? 's' : ''} sent!
            </div>
            <p style={{ fontSize: 12, color: 'var(--ink3)' }}>
              {sentCount} sub{sentCount !== 1 ? 's' : ''} will receive a magic-link email to join this project.
            </p>
            <button
              className="btn b-dk"
              onClick={onClose}
              data-testid="invite-sub-done-btn"
              type="button"
            >
              Done
            </button>
          </div>
        ) : (
          <>
            {/* ── Tabs ── */}
            <div style={{
              display: 'flex', borderBottom: '1px solid var(--line)',
              padding: '0 16px',
            }}>
              {(['email', 'book'] as Tab[]).map(t => (
                <button
                  key={t}
                  type="button"
                  onClick={() => setTab(t)}
                  data-testid={`invite-tab-${t}`}
                  style={{
                    background: 'none', border: 'none', padding: '8px 12px',
                    fontSize: 12, fontWeight: tab === t ? 600 : 400, cursor: 'pointer',
                    color: tab === t ? 'var(--ink)' : 'var(--ink4)',
                    borderBottom: tab === t ? '2px solid var(--ink)' : '2px solid transparent',
                    marginBottom: -1, fontFamily: 'inherit',
                  }}
                >
                  {t === 'email' ? 'Invite by email' : 'Address book'}
                </button>
              ))}
            </div>

            {/* ── By Email tab ── */}
            {tab === 'email' && (
              <div style={{ padding: 16 }}>
                <p style={{ fontSize: 12, color: 'var(--ink3)', marginBottom: 12 }}>
                  Enter their email and company — they'll get a magic-link to join this project.
                </p>
                <div style={{ marginBottom: 12 }}>
                  <label style={{ display: 'block', fontSize: 11, fontWeight: 600, marginBottom: 4, color: 'var(--ink3)' }}>
                    Email address <span style={{ color: 'var(--red)' }}>*</span>
                  </label>
                  <input
                    className="fld-i"
                    type="email"
                    placeholder="sub@example.com"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    data-testid="invite-email-input"
                    style={{ width: '100%' }}
                  />
                </div>
                <div style={{ marginBottom: 12 }}>
                  <label style={{ display: 'block', fontSize: 11, fontWeight: 600, marginBottom: 4, color: 'var(--ink3)' }}>
                    Company name <span style={{ color: 'var(--red)' }}>*</span>
                  </label>
                  <input
                    className="fld-i"
                    type="text"
                    placeholder="Acme Electrical LLC"
                    value={company}
                    onChange={e => setCompany(e.target.value)}
                    data-testid="invite-company-input"
                    style={{ width: '100%' }}
                  />
                </div>
                {err && (
                  <p style={errStyle} data-testid="invite-sub-error">{err}</p>
                )}
              </div>
            )}

            {/* ── Address Book tab ── */}
            {tab === 'book' && (
              <>
                <div style={{ padding: '12px 16px 0' }}>
                  <input
                    className="fld-i"
                    placeholder="Search by company, contact, or trade…"
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    data-testid="invite-sub-search"
                    style={{ width: '100%' }}
                  />
                </div>
                <div style={{ maxHeight: 260, overflowY: 'auto', padding: '8px 0' }}>
                  {isPending && (
                    <div style={{ padding: 16, fontSize: 12, color: 'var(--ink4)' }}>
                      Loading address book…
                    </div>
                  )}
                  {!isPending && filtered.length === 0 && (
                    <div style={{ padding: 16, fontSize: 12, color: 'var(--ink4)', textAlign: 'center' }}>
                      {vendors.length === 0
                        ? 'Address book is empty. Use the "Invite by email" tab.'
                        : 'No vendors match your search.'}
                    </div>
                  )}
                  {filtered.map((v: ContractorVendor) => {
                    const checked = selected.includes(v.id)
                    return (
                      <div
                        key={v.id}
                        className={`pm-sub-row${checked ? ' selected' : ''}`}
                        onClick={() => toggleVendor(v.id)}
                        role="checkbox"
                        aria-checked={checked}
                        tabIndex={0}
                        data-testid="invite-sub-vendor-row"
                        onKeyDown={e => {
                          if (e.key === ' ' || e.key === 'Enter') {
                            e.preventDefault()
                            toggleVendor(v.id)
                          }
                        }}
                        style={{ cursor: 'pointer' }}
                      >
                        <div
                          style={{
                            ...chkBase,
                            ...(checked ? { background: 'var(--ink)', color: '#fff', borderColor: 'var(--ink)' } : {}),
                          }}
                          aria-hidden="true"
                        >
                          {checked ? '✓' : ''}
                        </div>
                        <div>
                          <div className="pm-sub-nm">{v.company_name}</div>
                          <div className="pm-sub-meta">
                            {[v.contact_name, v.trade, v.email].filter(Boolean).join(' · ')}
                          </div>
                        </div>
                      </div>
                    )
                  })}
                </div>
                {err && (
                  <p style={errStyle} data-testid="invite-sub-error">{err}</p>
                )}
              </>
            )}

            {/* ── Footer ── */}
            <div style={ftrStyle}>
              <button
                className="btn b-gh btn-sm"
                onClick={onClose}
                data-testid="invite-sub-cancel-btn"
                type="button"
              >
                Cancel
              </button>
              {tab === 'email' ? (
                <button
                  className="btn b-dk btn-sm"
                  onClick={() => { void handleEmailInvite() }}
                  disabled={!email.trim() || !company.trim() || sending}
                  data-testid="invite-sub-send-btn"
                  type="button"
                >
                  {sending ? 'Sending…' : 'Send invite'}
                </button>
              ) : (
                <button
                  className="btn b-dk btn-sm"
                  onClick={() => { void handleBookInvite() }}
                  disabled={!selected.length || bookSend}
                  data-testid="invite-sub-send-btn"
                  type="button"
                >
                  {bookSend
                    ? 'Sending…'
                    : selected.length
                      ? `Invite ${selected.length} sub${selected.length !== 1 ? 's' : ''}`
                      : 'Invite subs'}
                </button>
              )}
            </div>
          </>
        )}
      </div>
    </ModalPortal>
  )
}
